/**
 * The client half of an LTI launch (TECH-3186).
 *
 * By the time anything here runs, the server has already done every hard part:
 * verified the platform's `id_token`, resolved which Workspace and which User
 * the launch names, confirmed the Participation, and parked the answer behind a
 * one-time code (TECH-3184). What is left is four steps in a fixed order (redeem,
 * sign in, point the app at the right Workspace, go) plus the two things that
 * order exists to protect.
 *
 * ## The code is spent on the first attempt, so there is exactly one attempt
 *
 * `/lti/exchange` consumes the code before it mints anything, which makes a
 * second POST with the same code indistinguishable from a replay, so it answers
 * 410. React's StrictMode mounts every component twice in development, and a
 * route remount is ordinary in production, so a naive effect would burn the code
 * on the first pass and render `code_invalid` on the second. `inFlight` keys the
 * whole flow by code and hands a repeat caller the SAME promise, so the second
 * mount observes the first mount's result rather than starting a second launch.
 *
 * ## The pool is set before the token is spent
 *
 * The custom token is minted inside one identity home's GCIP tenant
 * (TECH-3351), and Firebase decides which pool a credential belongs to when the
 * credential is exchanged. So `setSignInTenant` runs between the exchange and
 * the sign-in, and a launch is the one entry point whose pool does not come from
 * the address in the bar.
 *
 * ## This is the only place a sign-in can land on top of another one
 *
 * Every other entry point renders a login form only when nobody is signed in, so
 * "someone else is already here" was never a state the stores had to survive. A
 * launch arrives with its own credential and no such gate. A shared lab machine
 * with the previous student's session still live is the ordinary case, not the
 * exotic one. `signInWithCustomToken` swaps the Firebase user WITHOUT firing a
 * signed-out transition, so `auth-store`'s teardown never runs and the
 * previous account's drafts, grid layout and cached channels would survive into
 * the new session. Hence the explicit reset below, gated on the uid actually
 * changing so that a teacher relaunching the same course all day does not lose
 * their layout every time.
 */
import type {
	LaunchTarget,
	LtiExchangeErrorCode,
} from "@elaso/shared/types/lti";
import { exchangeLtiCode, LtiExchangeError } from "@/lib/api";
import { firebaseSession, setSignInTenant } from "@/lib/firebase";
import { SENTRY_ENABLED, sentryLogger } from "@/lib/sentry";

export type LtiLaunchResult =
	| { ok: true; target: LaunchTarget }
	| { ok: false; code: LtiExchangeErrorCode };

/** The shape `/lti/launch#code=…` carries. Same 43 chars the server mints. */
const CODE_SHAPE = /^[A-Za-z0-9_-]{43}$/;
const LEADING_HASH = /^#/;

// The code, remembered for the lifetime of this page. The landing route can be
// remounted mid-exchange (the root layout swaps the Outlet for a loader during
// router transitions), and the remounted instance reads the fragment AFTER the
// first instance stripped it. Without this memory it takes the no-code branch
// and paints `code_invalid` over a launch that is succeeding in the background.
// A real reload resets module state, so a reloaded spent launch still shows the
// reuse page; only same-page remounts rejoin.
let takenCode: string | null = null;

/**
 * Pull the handoff code out of the URL fragment and remove it from the address
 * bar in the same breath.
 *
 * A fragment never reaches a server, not ours, not a proxy's access log, and not
 * a `Referer` header. That is why TECH-3184 put the code there instead of in a
 * query string. That guarantee ends at the browser. Left in place, the code sits
 * in history, in a bookmark, and in whatever the address bar syncs. So it is
 * stripped on sight, before the network call that spends it, using
 * `replaceState` so no history entry is created (a `pushState` would leave the
 * back button pointing at a launch that can never be replayed).
 *
 * Returns null when there is no code, which on this route means somebody opened
 * it directly rather than arriving from a launch.
 */
export function takeHandoffCode(): string | null {
	if (typeof window === "undefined") {
		return null;
	}

	const { hash, pathname, search } = window.location;
	const code = new URLSearchParams(hash.replace(LEADING_HASH, "")).get("code");

	if (hash) {
		window.history.replaceState(window.history.state, "", pathname + search);
	}

	if (code && CODE_SHAPE.test(code)) {
		takenCode = code;
		return code;
	}
	if (hash) {
		// A fragment that yields no valid code is a NEW (malformed) arrival, not
		// a remount of the current launch. It must be refused, and the memory
		// must not mask that refusal by replaying the previous launch's result.
		takenCode = null;
		return null;
	}
	return takenCode;
}

const inFlight = new Map<string, Promise<LtiLaunchResult>>();

/**
 * Point the app at the Workspace this launch resolved, or at none for a launch
 * onto a Stub Workspace Entry, replacing whatever was persisted.
 *
 * The connect chain reads `currentWorkspaceId` (`core-store`'s
 * `pickCurrentWorkspace`) and only falls back to the first Workspace in the list
 * when the persisted one is not one the user holds. Setting it here, before
 * `/app` mounts, is therefore the whole mechanism. A launch from a Biology
 * course opens Biology, not whichever Workspace this browser happened to be
 * looking at yesterday.
 */
export const selectLaunchedWorkspace = async (
	target: LaunchTarget
): Promise<void> => {
	const { uiStore } = await import("@/lib/stores/ui-store");
	switch (target.kind) {
		case "workspace":
			uiStore.getState().setCurrentWorkspaceId(target.workspaceId);
			return;
		case "stub":
			uiStore.getState().setCurrentWorkspaceId(null);
			return;
		default: {
			const unreachable: never = target;
			throw new Error(`Unknown launch target: ${JSON.stringify(unreachable)}`);
		}
	}
};

const runLaunch = async (code: string): Promise<LtiLaunchResult> => {
	// Read BEFORE the sign-in that replaces it.
	const previousUid = firebaseSession.getCurrentUserId();

	const { customToken, target, gcipTenantId } = await exchangeLtiCode(code);
	// BEFORE the sign-in, always. The token was minted inside the launching
	// Organisation's identity home's pool, and Firebase reads `tenantId` at the
	// moment a credential is exchanged, so a client still pointed at the
	// project-wide pool refuses a token that is perfectly good
	// (docs/adr/33-identity-homes-sign-in-chosen-by-the-address.md, TECH-3351).
	// The sign-in page learns its pool from the address; a launch lands on the
	// tool's own URL, so the exchange answer is where this one comes from.
	setSignInTenant(gcipTenantId);
	const uid = await firebaseSession.loginWithCustomToken(customToken);

	// The custom-token sign-in swaps the user without a signed-out transition, so
	// the auth store's sign-out reset never runs for it.
	if (previousUid && previousUid !== uid) {
		sentryLogger.info("LTI launch replaced a signed-in session");
		const { resetAccountState } = await import("@/lib/stores/account-state");
		await resetAccountState();
	}

	await selectLaunchedWorkspace(target);
	return { ok: true, target };
};

/**
 * Run a launch to completion, or to the one code that says why it stopped.
 *
 * Never rejects. Every failure is a code the error page can render, because the
 * caller is a mount effect with nowhere useful to throw.
 */
export function completeLtiLaunch(code: string): Promise<LtiLaunchResult> {
	const existing = inFlight.get(code);
	if (existing) {
		return existing;
	}

	const attempt = runLaunch(code).catch((error): LtiLaunchResult => {
		if (error instanceof LtiExchangeError) {
			sentryLogger.warn("LTI launch refused", { code: error.code });
			return { ok: false, code: error.code };
		}
		// Firebase refused the custom token, or a store failed to load. Nothing
		// the user launching can act on, and nothing worth naming to them.
		if (SENTRY_ENABLED) {
			import("@sentry/react").then(({ captureException }) => {
				captureException(error, { tags: { context: "lti_launch" } });
			});
		}
		sentryLogger.error("LTI launch failed after the exchange", {
			message: error instanceof Error ? error.message : String(error),
		});
		return { ok: false, code: "internal" };
	});

	inFlight.set(code, attempt);
	return attempt;
}

/** Test seam. The in-flight map and taken code are module state and outlive a test. */
export const __resetLtiLaunchFlow = (): void => {
	inFlight.clear();
	takenCode = null;
};
